import { toast } from "react-toastify";

/**
 * Parses and decodes a JWT token payload
 * @param {string} token 
 * @returns {object|null}
 */
export const parseJwt = (token) => {
  if (!token || typeof token !== "string") return null;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

/**
 * Checks if a JWT token is structurally valid and unexpired
 * @param {string} token 
 * @returns {boolean}
 */
export const isTokenValid = (token) => {
  if (!token) return false;
  const payload = parseJwt(token);
  if (!payload) return false;
  // If exp exists in JWT, check that current time has not passed it
  if (payload.exp && payload.exp * 1000 <= Date.now()) {
    return false;
  }
  return true;
};

/**
 * Retrieves the stored JWT token from localStorage if valid
 * @returns {string|null}
 */
export const getAuthToken = () => {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const token = parsed?.token || parsed?.Token;
    if (token && isTokenValid(token)) {
      return token;
    }
    // If token exists but expired, clean it up
    if (token && !isTokenValid(token)) {
      localStorage.removeItem("user");
    }
    return null;
  } catch (e) {
    return null;
  }
};

/**
 * Retrieves the current authenticated user profile from localStorage
 * @returns {object|null}
 */
export const getAuthUser = () => {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const token = parsed?.token || parsed?.Token;
    if (token && isTokenValid(token)) {
      return parsed;
    }
    localStorage.removeItem("user");
    return null;
  } catch (e) {
    return null;
  }
};

/**
 * Checks if current user is logged in with a valid unexpired JWT
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  return !!getAuthToken();
};

/**
 * Attaches Authorization Bearer header if a valid token is present
 * @param {object} customHeaders 
 * @returns {object}
 */
export const getAuthHeaders = (customHeaders = {}) => {
  const token = getAuthToken();
  const headers = { ...customHeaders };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
};

/**
 * Clears stored authentication session
 */
export const logoutUser = () => {
  try {
    localStorage.removeItem("user");
  } catch (e) {
    console.warn("Could not remove user from localStorage:", e);
  }
};

/**
 * Handles HTTP 401 Unauthorized API responses
 */
export const handleAuthError = (customMessage) => {
  logoutUser();
  toast.error(customMessage || "Session expired or unauthorized. Please log in again.");
  setTimeout(() => {
    window.location.href = "/equity-login";
  }, 800);
};
